import PropTypes from "prop-types";

import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton";
import AddIcon from "@mui/icons-material/Add";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import AutoCompleteField from "components/_refactor/fields/autocomplete";
import DateField from "components/_refactor/fields/date";
import CurrencyField from "components/_refactor/fields/number";
import InputText from "components/_refactor/text/input";

import { MP } from "./mpConstants";
import { formatConcorrenteRowLabel } from "./mpMappers";
import { formatBRL, resolvePrecoMarcaNumerico } from "./mpUtils";
import SectionTitle from "./SectionTitle";
import TipoColetaCard from "./TipoColetaCard";

export default function MonitoramentoPrecoFormSections({ classes, form }) {
  const {
    tipoColeta,
    setTipoColeta,
    dataColeta,
    setDataColeta,
    representante,
    razaoSocial,
    handleRazaoSocialChange,
    lojaCompleta,
    setLojaCompleta,
    razoesOptions,
    lojaGrupoOptions,
    marcaReferenciaId,
    marcasReferencia,
    marcaReferenciaSelecionada,
    handleMarcaReferenciaChange,
    produto,
    handleProdutoChange,
    produtosCatalogo,
    precoMarcaLoja,
    setPrecoMarcaLoja,
    concorrentesRows,
    concorrenteDestaqueId,
    updateConcorrente,
    handleFotoChange,
    uploadingFotoIndex,
    coletas,
    handleEditColeta,
    handleRemoveColeta,
    handleAdicionarColeta,
    handleFinalizar,
    isCurrentFormValid,
    finalizando,
    onAbrirCadastroConcorrente,
    podeAdicionarConcorrente,
  } = form;

  return (
    <>
      <section className={classes.formSection}>
        <SectionTitle number={0} title="Tipo de coleta" classes={classes} />
        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
            <div className={classes.tipoGrid}>
              <TipoColetaCard
                classes={classes}
                selected={tipoColeta === "padrao"}
                title="Padrão (aberta)"
                subtitle="Você seleciona a loja e os produtos."
                onSelect={() => setTipoColeta("padrao")}
              />
              <TipoColetaCard
                classes={classes}
                selected={tipoColeta === "direcionada"}
                title="Direcionada (específica)"
                subtitle="Coleta definida por outro time."
                onSelect={() => setTipoColeta("direcionada")}
                disabled
              />
            </div>
          </Grid>
          {tipoColeta === "direcionada" && (
            <Grid item xs={12} md={5}>
              <Box style={{ paddingTop: 4 }}>
                <Typography
                  variant="body2"
                  style={{ color: MP.textMuted, fontWeight: 600 }}
                >
                  Coleta direcionada (opcional)
                </Typography>
                <InputText
                  value=""
                  disabled
                  placeholder="Selecione a coleta atribuída"
                />
              </Box>
            </Grid>
          )}
        </Grid>
      </section>

      <section className={classes.formSection}>
        <SectionTitle number={1} title="Identificação" classes={classes} />
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <DateField
              label="Data da coleta"
              value={dataColeta}
              onChange={setDataColeta}
              pattern="br"
              initialViewDate={new Date()}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <InputText label="Representante" value={representante} disabled />
          </Grid>
        </Grid>
      </section>

      <section className={classes.formSection}>
        <SectionTitle number={2} title="Dados da loja" classes={classes} />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={12}>
            <AutoCompleteField
              label="Razão Social"
              options={razoesOptions}
              value={razaoSocial}
              onChange={handleRazaoSocialChange}
              optionLabelKey="razaoSocialLabel"
              optionIdKey="razaoSocial"
              required
              ignoreCnpjPunctuationInSearch
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <AutoCompleteField
              label="Grupo econômico"
              options={lojaGrupoOptions}
              value={lojaCompleta}
              onChange={setLojaCompleta}
              optionLabelKey="grupoEconomicoExibicao"
              optionIdKey="id"
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InputText
              label="Cidade"
              value={lojaCompleta?.cidade ?? ""}
              disabled
              required
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InputText
              label="Estado (UF)"
              value={lojaCompleta?.uf ?? ""}
              disabled
              required
            />
          </Grid>
        </Grid>
      </section>

      <section className={classes.formSection}>
        <SectionTitle
          number={3}
          title="Produto de referência"
          classes={classes}
        />
        <Grid
          container
          spacing={3}
          alignItems="flex-start"
          className={classes.produtoRefGrid}
        >
          <Grid item xs={12} md={6}>
            <AutoCompleteField
              label="Marca"
              options={marcasReferencia}
              value={marcaReferenciaSelecionada}
              onChange={handleMarcaReferenciaChange}
              optionLabelKey="nome"
              optionIdKey="idMarca"
              required
            />
          </Grid>
          {!produto ? (
            <Grid item xs={12} md={6}>
              <AutoCompleteField
                label="SKU"
                options={produtosCatalogo}
                value={produto}
                onChange={handleProdutoChange}
                optionLabelKey="nome"
                optionIdKey="id"
                disabled={!marcaReferenciaId}
                required
              />
            </Grid>
          ) : (
            <>
              <Grid item xs={12} md={6}>
                <AutoCompleteField
                  label="SKU"
                  options={produtosCatalogo}
                  value={produto}
                  onChange={handleProdutoChange}
                  optionLabelKey="nome"
                  optionIdKey="id"
                  style={{ width: "100%" }}
                  disabled={!marcaReferenciaId}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <div className={classes.produtoColMeta}>
                  <div className={classes.produtoMetaLine}>
                    <span className={classes.produtoMetaLabel}>Marca</span>
                    <span className={classes.produtoMetaValue}>
                      {marcaReferenciaSelecionada?.nome || produto.marca || "—"}
                    </span>
                  </div>
                  <div className={classes.produtoMetaLine}>
                    <span className={classes.produtoMetaLabel}>
                      Nome do produto
                    </span>
                    <span className={classes.produtoMetaValue}>
                      {produto.nome}
                    </span>
                  </div>
                  <div className={classes.produtoMetaLine}>
                    <span className={classes.produtoMetaLabel}>Tamanho</span>
                    <span className={classes.produtoMetaValue}>
                      {produto.tamanho || "—"}
                    </span>
                  </div>
                  <div className={classes.produtoMetaLine}>
                    <span className={classes.produtoMetaLabel}>
                      Tecido base
                    </span>
                    <span className={classes.produtoMetaValue}>
                      {produto.tecidoBase || produto.gramatura}
                    </span>
                  </div>
                </div>
              </Grid>
              <Grid item xs={12} md={6}>
                <div className={classes.produtoColPreco}>
                  <CurrencyField
                    key={produto.id}
                    label={`Preço ${
                      marcaReferenciaSelecionada?.nome ||
                      produto.marca ||
                      "na loja"
                    } na loja (R$)`}
                    required
                    value={
                      precoMarcaLoja === undefined ||
                      precoMarcaLoja === null ||
                      precoMarcaLoja === ""
                        ? ""
                        : precoMarcaLoja
                    }
                    onChange={(v) =>
                      setPrecoMarcaLoja(
                        v.floatValue === undefined ? "" : v.floatValue,
                      )
                    }
                  />
                  <Typography
                    variant="caption"
                    style={{
                      color: MP.textMuted,
                      display: "block",
                      marginTop: 8,
                      lineHeight: 1.45,
                    }}
                  >
                    Informe o preço de venda do produto da marca selecionada
                    nesta loja.
                  </Typography>
                </div>
              </Grid>
            </>
          )}
        </Grid>
      </section>

      <section className={classes.formSection}>
        <SectionTitle
          number={4}
          title="Concorrentes – informar preços e fotos"
          onClickButton={onAbrirCadastroConcorrente}
          buttonText="+ Adicionar concorrente"
          buttonDisabled={!podeAdicionarConcorrente}
          classes={classes}
        />
        <div className={classes.tableScroll}>
          <Table className={classes.table} size="small">
            <TableHead>
              <TableRow>
                <TableCell component="th">Concorrente</TableCell>
                <TableCell component="th">Produto encontrado?</TableCell>
                <TableCell component="th">Preço normal (R$)</TableCell>
                <TableCell component="th">Em promoção?</TableCell>
                <TableCell component="th">Preço promocional (R$)</TableCell>
                <TableCell component="th">Foto (produto/etiqueta)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {concorrentesRows.map((row, index) => (
                <TableRow
                  key={
                    row.idProdutoConcorrente != null
                      ? `pc-${row.idProdutoConcorrente}`
                      : `${row.nome}-${index}`
                  }
                  className={
                    concorrenteDestaqueId != null &&
                    row.idProdutoConcorrente === concorrenteDestaqueId
                      ? classes.concorrenteRowDestaque
                      : undefined
                  }
                >
                  <TableCell>
                    <span className={classes.concorrenteNome}>
                      {formatConcorrenteRowLabel(row)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <FormControl component="fieldset">
                      <RadioGroup
                        row
                        value={row.encontrado ? "sim" : "nao"}
                        onChange={(e) =>
                          updateConcorrente(index, {
                            encontrado: e.target.value === "sim",
                          })
                        }
                      >
                        <FormControlLabel
                          className={classes.radioLabel}
                          value="sim"
                          control={<Radio color="primary" size="small" />}
                          label="Sim"
                        />
                        <FormControlLabel
                          className={classes.radioLabel}
                          value="nao"
                          control={<Radio color="primary" size="small" />}
                          label="Não"
                        />
                      </RadioGroup>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    {row.encontrado ? (
                      <CurrencyField
                        label=""
                        value={row.precoNormal}
                        onChange={(v) =>
                          updateConcorrente(index, {
                            precoNormal: v.floatValue,
                          })
                        }
                      />
                    ) : (
                      <span className={classes.dashCell}>—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {row.encontrado ? (
                      <FormControl component="fieldset">
                        <RadioGroup
                          row
                          value={row.emPromocao ? "sim" : "nao"}
                          onChange={(e) =>
                            updateConcorrente(index, {
                              emPromocao: e.target.value === "sim",
                              precoPromocional:
                                e.target.value === "sim"
                                  ? row.precoPromocional
                                  : undefined,
                            })
                          }
                        >
                          <FormControlLabel
                            className={classes.radioLabel}
                            value="sim"
                            control={<Radio color="primary" size="small" />}
                            label="Sim"
                          />
                          <FormControlLabel
                            className={classes.radioLabel}
                            value="nao"
                            control={<Radio color="primary" size="small" />}
                            label="Não"
                          />
                        </RadioGroup>
                      </FormControl>
                    ) : (
                      <span className={classes.dashCell}>—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {row.encontrado ? (
                      row.emPromocao ? (
                        <CurrencyField
                          label=""
                          value={row.precoPromocional}
                          onChange={(v) =>
                            updateConcorrente(index, {
                              precoPromocional: v.floatValue,
                            })
                          }
                        />
                      ) : (
                        <span className={classes.dashCell}>—</span>
                      )
                    ) : (
                      <span className={classes.dashCell}>—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {row.encontrado ? (
                      <Box
                        display="flex"
                        flexDirection="column"
                        style={{ gap: 8 }}
                      >
                        <Button
                          variant="outlined"
                          component="label"
                          size="small"
                          className={classes.fotoBtn}
                          disabled={uploadingFotoIndex === index}
                          startIcon={
                            uploadingFotoIndex === index ? (
                              <CircularProgress size={14} color="inherit" />
                            ) : null
                          }
                        >
                          {uploadingFotoIndex === index
                            ? "Processando..."
                            : row.fotoBase64
                              ? "Trocar foto"
                              : "Enviar foto"}
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            capture="environment"
                            hidden
                            disabled={uploadingFotoIndex === index}
                            onChange={(e) => {
                              handleFotoChange(
                                index,
                                e.target.files?.[0] || null,
                              );
                              e.target.value = "";
                            }}
                          />
                        </Button>
                        {row.fotoBase64 ? (
                          <>
                            <img
                              src={row.fotoBase64}
                              alt={`Etiqueta ${row.nome}`}
                              className={classes.thumb}
                            />
                            <Button
                              size="small"
                              color="secondary"
                              onClick={() => handleFotoChange(index, null)}
                              disabled={uploadingFotoIndex === index}
                            >
                              Remover foto
                            </Button>
                          </>
                        ) : (
                          <Typography
                            variant="caption"
                            style={{ color: MP.textMuted }}
                          >
                            Máx. 5 MB · JPEG, PNG ou WebP
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <span className={classes.dashCell}>—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className={classes.infoCallout}>
          <InfoOutlinedIcon style={{ color: MP.primary, marginTop: 2 }} />
          <div>
            <div className={classes.infoCalloutTitle}>Dica</div>
            <div className={classes.infoCalloutBody}>
              Selecione &quot;Não&quot; se o produto do concorrente não estiver
              disponível nesta loja. Campos de preço, promoção e foto ficam
              ocultos nesse caso. No celular, em &quot;Enviar foto&quot;, o
              navegador pode abrir a câmera (traseira) ou a galeria, conforme o
              aparelho e o sistema.
            </div>
          </div>
        </div>
      </section>

      <div className={classes.listActionsRow}>
        <div className={classes.listMain}>
          <section className={classes.formSection} style={{ marginBottom: 0 }}>
            <div className={classes.sectionTitleRow}>
              <div className={classes.sectionBadge}>{coletas.length}</div>
              <Typography component="h2" className={classes.sectionTitleText}>
                Produtos da coleta (sessão atual)
              </Typography>
            </div>
            <div className={classes.tableScroll}>
              <Table className={classes.table} size="small">
                <TableHead>
                  <TableRow>
                    <TableCell component="th" width={56}>
                      #
                    </TableCell>
                    <TableCell component="th" align="center">
                      Marca
                    </TableCell>
                    <TableCell component="th" align="center">
                      SKU
                    </TableCell>
                    <TableCell component="th" align="center">
                      Produto
                    </TableCell>
                    <TableCell component="th" align="center">
                      Preço na loja (R$)
                    </TableCell>
                    <TableCell component="th" align="center">
                      Ações
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {coletas.map((c, i) => (
                    <TableRow key={c.clientId ?? c.id}>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>
                        {c.marcaReferencia ?? c.produto?.marca ?? "—"}
                      </TableCell>
                      <TableCell>{c.produto?.sku ?? "—"}</TableCell>
                      <TableCell>{c.produto?.nome ?? "—"}</TableCell>
                      <TableCell>
                        {formatBRL(
                          resolvePrecoMarcaNumerico(
                            c.precoMarcaLoja ?? c.precoKarstenLoja,
                          ),
                        )}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          className={classes.iconAction}
                          aria-label="Editar"
                          onClick={() => handleEditColeta(c)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          className={classes.iconDelete}
                          aria-label="Excluir"
                          onClick={() => handleRemoveColeta(c.clientId ?? c.id)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <p className={classes.footerNote}>
              Total de produtos cadastrados: {coletas.length}
            </p>
          </section>
        </div>
        <aside className={classes.listSidebar}>
          <div className={classes.sidebarCard}>
            <div className={classes.sidebarHeader}>
              <AssignmentIcon style={{ fontSize: 22 }} />
              <span>Ações da coleção</span>
            </div>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              className={classes.btnPrimaryFull}
              startIcon={<AddIcon />}
              onClick={handleAdicionarColeta}
              disabled={!isCurrentFormValid}
            >
              Adicionar novo produto
            </Button>
            <Button
              fullWidth
              variant="outlined"
              color="primary"
              className={classes.btnOutlineFull}
              startIcon={<CheckCircleIcon />}
              onClick={handleFinalizar}
              disabled={!coletas.length || finalizando}
            >
              Finalizar coleta
            </Button>
          </div>
        </aside>
      </div>

      <div className={classes.infoCallout} style={{ marginTop: 8 }}>
        <InfoOutlinedIcon style={{ color: MP.primary, marginTop: 2 }} />
        <div>
          <div className={classes.infoCalloutTitle}>Importante</div>
          <div className={classes.infoCalloutBody}>
            Você pode adicionar vários produtos à coleta. Cada produto será
            salvo como um item separado. Os itens permanecem salvos neste
            navegador até você finalizar o envio.
          </div>
        </div>
      </div>
    </>
  );
}

MonitoramentoPrecoFormSections.propTypes = {
  classes: PropTypes.object.isRequired,
  form: PropTypes.shape({
    tipoColeta: PropTypes.string.isRequired,
    setTipoColeta: PropTypes.func.isRequired,
    dataColeta: PropTypes.string.isRequired,
    setDataColeta: PropTypes.func.isRequired,
    representante: PropTypes.string.isRequired,
    razaoSocial: PropTypes.object,
    handleRazaoSocialChange: PropTypes.func.isRequired,
    lojaCompleta: PropTypes.object,
    setLojaCompleta: PropTypes.func.isRequired,
    razoesOptions: PropTypes.array.isRequired,
    lojaGrupoOptions: PropTypes.array.isRequired,
    marcaReferenciaId: PropTypes.string,
    marcasReferencia: PropTypes.array.isRequired,
    marcaReferenciaSelecionada: PropTypes.object,
    handleMarcaReferenciaChange: PropTypes.func.isRequired,
    produto: PropTypes.object,
    handleProdutoChange: PropTypes.func.isRequired,
    produtosCatalogo: PropTypes.array.isRequired,
    precoMarcaLoja: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    setPrecoMarcaLoja: PropTypes.func.isRequired,
    concorrentesRows: PropTypes.array.isRequired,
    concorrenteDestaqueId: PropTypes.number,
    updateConcorrente: PropTypes.func.isRequired,
    handleFotoChange: PropTypes.func.isRequired,
    uploadingFotoIndex: PropTypes.number,
    coletas: PropTypes.array.isRequired,
    handleEditColeta: PropTypes.func.isRequired,
    handleRemoveColeta: PropTypes.func.isRequired,
    handleAdicionarColeta: PropTypes.func.isRequired,
    handleFinalizar: PropTypes.func.isRequired,
    isCurrentFormValid: PropTypes.bool.isRequired,
    finalizando: PropTypes.bool.isRequired,
    onAbrirCadastroConcorrente: PropTypes.func.isRequired,
  }).isRequired,
};
